import { Box, Stack } from "@chakra-ui/react";
import { DndContext, DragEndEvent, PointerSensor, closestCenter, useSensor, useSensors } from "@dnd-kit/core";
import { SortableContext, verticalListSortingStrategy } from "@dnd-kit/sortable";

import AddFieldButton from "./AddFieldButton";
import FieldCard from "./FieldCard";
import InstructionsCard from "../InstructionsCard";
import ResponseScreenCard from "../ResponseScreenCard";
import type { EditorField } from "../../hooks/useCustomFormEditorState";
import type { CustomFormFieldType } from "../../types";

interface Props {
  fields: EditorField[];
  instructions: string;
  responseScreenHeader: string | null;
  responseScreenText: string | null;
  selectedIndex: number | null;
  onInstructionsChange: (v: string) => void;
  onResponseScreenHeaderChange: (v: string) => void;
  onResponseScreenTextChange: (v: string) => void;
  onAddField: (type: CustomFormFieldType) => void;
  onSelect: (index: number | null) => void;
  onReorder: (fromIndex: number, toIndex: number) => void;
  onDuplicate: (index: number) => void;
  onDelete: (index: number) => void;
}

const FIELD_ID = (index: number) => `field:${index}`;

export default function FieldCanvas({
  fields, instructions, responseScreenHeader, responseScreenText, selectedIndex,
  onInstructionsChange, onResponseScreenHeaderChange, onResponseScreenTextChange,
  onAddField, onSelect, onReorder, onDuplicate, onDelete,
}: Props) {
  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 5 } }));

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const from = Number(String(active.id).replace("field:", ""));
    const to = Number(String(over.id).replace("field:", ""));
    if (Number.isNaN(from) || Number.isNaN(to)) return;
    onReorder(from, to);
  };

  return (
    <Box flex={1} maxW="720px" onClick={() => onSelect(null)} pt={2}>
      <Stack spacing={4}>
        <Box onClick={(e) => e.stopPropagation()}>
          <InstructionsCard value={instructions} onChange={onInstructionsChange} />
        </Box>

        <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
          <SortableContext
            items={fields.map((_, i) => FIELD_ID(i))}
            strategy={verticalListSortingStrategy}
          >
            <Stack spacing={3}>
              {fields.map((field, index) => (
                <Box key={field.clientKey} data-field-key={field.clientKey} onClick={(e) => e.stopPropagation()}>
                  <FieldCard
                    sortableId={FIELD_ID(index)}
                    field={field}
                    isSelected={selectedIndex === index}
                    onSelect={() => onSelect(index)}
                    onDuplicate={() => onDuplicate(index)}
                    onDelete={() => onDelete(index)}
                  />
                </Box>
              ))}
            </Stack>
          </SortableContext>
        </DndContext>

        <Box onClick={(e) => e.stopPropagation()}>
          <AddFieldButton onAdd={onAddField} />
        </Box>

        <Box onClick={(e) => e.stopPropagation()}>
          <ResponseScreenCard
            header={responseScreenHeader}
            text={responseScreenText}
            onHeaderChange={onResponseScreenHeaderChange}
            onTextChange={onResponseScreenTextChange}
          />
        </Box>
      </Stack>
    </Box>
  );
}
